<template>
  <main>
    <section class="container">
      <div class="h-20"></div>
      
      <!--Tchat-->
      <div class="widget-chatbot">
        <div class="widget-chatbot-header">
          <div class="flex align-items-center">
            <img :src="agentAvatar" class="size-2xl rounded-100 mr-3" alt="Agent" />
            <span class="font-semibold text-base">{{ agentName }}</span>
          </div>
          <div>
            <h2 class="font-semibold">Live Tchat</h2>
          </div>
        </div>
        <div class="widget-chatbot-messages" ref="messagesContainer">
          <!-- Tags de réclamation -->
          <div class="widget-chatbot-tag" v-if="selectedTag">
            {{ selectedTag }}
          </div>

          <!-- Loading -->
          <template v-if="loading">
            <p class="text-center my-5">Chargement des messages...</p>
          </template>

          <!-- Messages -->
          <template v-else>
            <div 
              v-for="(message, index) in messages" 
              :key="message.id || index"
              :class="['message', message.qui]"
            >
              {{ message.texte }}
            </div>
          </template>

          <!-- Indication que l'agent écrit -->
          <div v-if="isAgentTyping" class="widget-chatbot-indication">
            <div class="loader-dots left"></div>
            {{ agentName }} est entrain d'ecrire...
          </div>
        </div>
        <div class="widget-chatbot-send-message">
          <div class="widget-chatbot-input-set">
            <input 
              v-model="newMessage"
              type="text" 
              class="widget-chatbot-input" 
              placeholder="Écrire un message..." 
              @keyup.enter="sendMessage"
            />
            <button 
              class="btn-ultra grey tips attachment"
              @click="attachFile"
            >
              <div class="btn-show">
                <img src="/public/icons/icon-attachment.svg" class="btn-icon" alt="" />
              </div>
              <div class="btn-loading">
                <div class="loader-dots"></div>
              </div>
            </button>
          </div>
          <button 
            class="btn-ultra tips send"
            :class="{ 'loading': isSending }"
            @click="sendMessage"
          >
            <div class="btn-show">
              <img src="/public/icons/icon-send.svg" class="btn-icon" alt="" />
            </div>
            <div class="btn-loading">
              <div class="loader-dots"></div>
            </div>
          </button>
        </div>
      </div>
    </section>
  </main>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, onUnmounted, nextTick, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import chatService, { type ChatMessage } from '@/services/chatService'
import { getSocket, initSocket, joinRoom, leaveRoom, onEvent, offEvent, isSocketConnected, updateSocketAuth } from '@/config/socket'

export default defineComponent({
  name: 'LiveTchatView',
  setup() {
    const route = useRoute()
    const router = useRouter()
    const messagesContainer = ref<HTMLElement | null>(null)
    const newMessage = ref('')
    const isSending = ref(false)
    const isAgentTyping = ref(false)
    const loading = ref(false)
    const conversationId = ref<number | null>(null)
    const agentName = ref('Service client')
    const agentAvatar = ref('/public/assets/tchat/600w/avatar.jpg')
    const selectedTag = ref<string>('')
    const conversationType = ref<string>('')
    
    // Messages
    const messages = ref<ChatMessage[]>([])
    
    // Référence au room actuel pour pouvoir le quitter
    let currentRoomName: string | null = null
    let messageListener: ((data: any) => void) | null = null
    let currentSocketInstance: any = null

    // Garder une trace des IDs de messages que nous venons d'envoyer pour ignorer les broadcasts
    const recentlySentMessageIds = ref<Set<number>>(new Set())
    // Garder une trace des textes de messages utilisateur récents pour ignorer les broadcasts
    const recentlySentMessageTexts = ref<Set<string>>(new Set())

    /**
     * Faire défiler vers le bas pour voir le dernier message
     */
    const scrollToBottom = async () => {
      await nextTick()
      if (messagesContainer.value) {
        messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight
      }
    }

    /**
     * Charger les messages de la conversation
     */
    const loadMessages = async (id: number) => {
      loading.value = true
      try {
        const response = await chatService.getMessages(id)
        if (response.data && Array.isArray(response.data)) {
          messages.value = response.data.map((msg: ChatMessage) => ({
            ...msg,
            type: msg.qui === 'user' ? 'user' : 'bot',
            text: msg.texte
          }))
          
          // Mettre à jour les infos de l'agent depuis le dernier message bot
          const allMessages = [...messages.value]
          const lastBotMessage = allMessages.reverse().find(m => m.qui === 'bot')
          if (lastBotMessage) {
            agentName.value = lastBotMessage.agentNom || 'Service client'
            agentAvatar.value = lastBotMessage.agentAvatar || '/public/assets/tchat/600w/avatar.jpg'
          }
          
          await scrollToBottom()
        }
      } catch (error) {
        console.error('Erreur lors du chargement des messages:', error)
      } finally {
        loading.value = false
      }
    }

    /**
     * Envoyer un message
     */
    const sendMessage = async () => {
      if (!newMessage.value.trim() || isSending.value) return

      const messageText = newMessage.value.trim()
      const tempMessageText = messageText
      newMessage.value = ''
      isSending.value = true

      // Ajouter le message de l'utilisateur temporairement
      const tempMessage = {
        id: 0,
        conversationId: conversationId.value || 0,
        texte: tempMessageText,
        date: new Date().toISOString(),
        statut: 'sent',
        qui: 'user',
        type: 'user',
        text: tempMessageText
      } as ChatMessage & { type: string, text: string }
      
      console.log('[sendMessage] 📤 Ajout du message temporaire:', {
        texte: tempMessageText,
        qui: tempMessage.qui,
        type: tempMessage.type,
        id: tempMessage.id,
        totalMessages: messages.value.length
      })
      
      messages.value.push(tempMessage)
      
      // Ajouter le texte à la liste des messages récemment envoyés pour ignorer le broadcast
      recentlySentMessageTexts.value.add(tempMessageText)
      console.log('[sendMessage] 📝 Texte ajouté à recentlySentMessageTexts:', tempMessageText)
      // Retirer après 10 secondes
      setTimeout(() => {
        recentlySentMessageTexts.value.delete(tempMessageText)
      }, 10000)

      await scrollToBottom()

      try {
        // Envoyer le message à l'API
        const response = await chatService.sendMessage({
          conversationId: conversationId.value || 0,
          texte: tempMessageText,
          conversationType: conversationType.value || selectedTag.value
        })

        if (response.data && response.data.message) {
          const apiMessage = response.data.message
          
          console.log('[sendMessage] ✅ Réponse API reçue:', {
            id: apiMessage.id,
            texte: apiMessage.texte,
            qui: apiMessage.qui,
            conversationId: apiMessage.conversationId,
            statut: apiMessage.statut,
            date: apiMessage.date
          })
          
          // Mettre à jour le message temporaire avec les données de l'API (notamment l'ID)
          // Chercher le dernier message temporaire avec le même texte
          let tempMessageIndex = -1
          for (let i = messages.value.length - 1; i >= 0; i--) {
            const m = messages.value[i]
            if (m.id === 0 && 
                ((m as any).text || m.texte) === messageText &&
                m.qui === 'user') {
              tempMessageIndex = i
              break
            }
          }
          
          console.log('[sendMessage] 🔍 Index du message temporaire trouvé:', tempMessageIndex)
          
          if (tempMessageIndex >= 0) {
            // Mettre à jour le message temporaire en place avec toutes les propriétés
            const messageType = apiMessage.qui === 'user' ? 'user' : 'bot'
            const updatedMessage = {
              ...apiMessage,
              type: messageType,
              text: apiMessage.texte
          } as ChatMessage & { type: string, text: string }
            
            console.log('[sendMessage] 🔄 Mise à jour du message temporaire:', {
              index: tempMessageIndex,
              ancienId: messages.value[tempMessageIndex].id,
              ancienQui: messages.value[tempMessageIndex].qui,
              ancienType: (messages.value[tempMessageIndex] as any).type,
              nouveauId: updatedMessage.id,
              nouveauQui: updatedMessage.qui,
              nouveauType: updatedMessage.type,
              texte: updatedMessage.texte
            })
            
            messages.value[tempMessageIndex] = updatedMessage
            
            console.log('[sendMessage] 📊 État après mise à jour:', {
              totalMessages: messages.value.length,
              dernierMessage: {
                id: messages.value[messages.value.length - 1]?.id,
                qui: messages.value[messages.value.length - 1]?.qui,
                type: (messages.value[messages.value.length - 1] as any)?.type,
                texte: messages.value[messages.value.length - 1]?.texte
              }
            })
            
            // Si c'est un message utilisateur, l'ajouter à la liste des messages récemment envoyés
            // pour ignorer le broadcast qui pourrait arriver après
            if (apiMessage.qui === 'user' && apiMessage.id) {
              console.log('[sendMessage] 🎯 Ajout de l\'ID à recentlySentMessageIds:', apiMessage.id)
              recentlySentMessageIds.value.add(apiMessage.id)
              // Retirer de la liste après 5 secondes
              setTimeout(() => {
                recentlySentMessageIds.value.delete(apiMessage.id)
                console.log('[sendMessage] 🗑️ Retrait de l\'ID de recentlySentMessageIds:', apiMessage.id)
              }, 5000)
              
              // Retirer aussi le texte de la liste car on a maintenant l'ID
              recentlySentMessageTexts.value.delete(messageText)
              console.log('[sendMessage] 🗑️ Retrait du texte de recentlySentMessageTexts:', messageText)
            } else {
              console.warn('[sendMessage] ⚠️ Message non-utilisateur ou sans ID:', {
                qui: apiMessage.qui,
                id: apiMessage.id
              })
            }
          } else {
            // Si le message temporaire n'existe plus ou n'a pas été trouvé, vérifier s'il n'existe pas déjà
            if (apiMessage.id) {
              const messageExists = messages.value.some(m => 
                m.id === apiMessage.id
              )
              if (!messageExists) {
                messages.value.push({
                  ...apiMessage,
                  type: apiMessage.qui === 'user' ? 'user' : 'bot',
                  text: apiMessage.texte
                } as ChatMessage & { type: string, text: string })
                
                // Si c'est un message utilisateur, l'ajouter à la liste des messages récemment envoyés
                if (apiMessage.qui === 'user') {
                  recentlySentMessageIds.value.add(apiMessage.id)
                  // Retirer de la liste après 5 secondes
                  setTimeout(() => {
                    recentlySentMessageIds.value.delete(apiMessage.id)
                  }, 5000)
                }
              }
            }
          }

          // Mettre à jour l'ID de conversation si c'était une nouvelle conversation
          if (response.data.conversationId && !conversationId.value) {
            conversationId.value = response.data.conversationId
            // Mettre à jour l'URL sans recharger la page
            router.replace(`/front/aide/live-tchat/${response.data.conversationId}`)
            // Configurer l'écoute Socket.IO pour la nouvelle conversation
            setupSocketListener()
          }

          await scrollToBottom()
        }
      } catch (error) {
        console.error('Erreur lors de l\'envoi du message:', error)
        // Retirer le message en cas d'erreur
        messages.value.pop()
      } finally {
        isSending.value = false
      }
    }

    /**
     * Attacher un fichier
     */
    const attachFile = () => {
      // TODO: Implémenter l'upload de fichier
      console.log('Attacher un fichier')
    }

    /**
     * Initialiser l'écoute des messages Socket.IO
     */
    const setupSocketListener = () => {
      if (!conversationId.value) {
        console.warn('[Socket.IO] ⚠️ Aucune conversation ID pour configurer l\'écoute')
        return
      }

      try {
        // Arrêter l'ancien listener s'il existe
        if (currentRoomName) {
          leaveRoom(currentRoomName)
          if (messageListener) {
            offEvent('new.message', messageListener)
            messageListener = null
          }
        }

        // Initialiser Socket.IO si nécessaire
        const currentSocket = initSocket()
        if (!currentSocket) {
          console.warn('[Socket.IO] ⚠️ Socket.IO non disponible pour cette conversation')
          return
        }
        
        // Stocker la référence au socket pour pouvoir retirer le listener plus tard
        currentSocketInstance = currentSocket

        // Mettre à jour l'authentification avec le token actuel
        updateSocketAuth(localStorage.getItem('auth_token'))

        // Rejoindre le room de conversation
        const roomName = `conversation.${conversationId.value}`
        currentRoomName = roomName
        
        // Créer le listener pour les nouveaux messages
        messageListener = (data: any) => {
          console.log('[Socket.IO Listener] 🔔 ÉVÉNEMENT REÇU - new.message déclenché!')
          console.log('[Socket.IO Listener] 📡 Message reçu:', data)
          console.log('[Socket.IO Listener] 🔍 Conversation ID actuelle:', conversationId.value)
          
          // Vérifier que le message appartient à la conversation actuelle
          const receivedConversationId = data.conversation_id || data.message?.conversationId || data.message?.conversation_id
          if (receivedConversationId && receivedConversationId !== conversationId.value) {
            console.log('[Socket.IO Listener] 🚫 Message ignoré (conversation différente):', {
              reçu: receivedConversationId,
              actuelle: conversationId.value
            })
            return
          }
          
          // Ajouter le nouveau message reçu
          // Socket.IO peut envoyer directement le message ou dans un objet 'message'
          const messageData = data.message || data
          if (messageData) {
            const newMessage: ChatMessage = {
              ...messageData
            }
            
            console.log('[Socket.IO Listener] 📨 Nouveau message créé:', {
              id: newMessage.id,
              texte: newMessage.texte,
              qui: newMessage.qui,
              conversationId: newMessage.conversationId
            })
            
            // Fonction helper pour obtenir le texte d'un message
            const getTexte = (msg: any) => msg.texte
            
            // Ignorer le message si c'est un message utilisateur que nous venons d'envoyer
            // Vérifier par ID d'abord
            if (newMessage.qui === 'user' && newMessage.id && recentlySentMessageIds.value.has(newMessage.id)) {
              console.log('[Socket.IO Listener] 🚫 Message ignoré (ID déjà dans recentlySentMessageIds):', newMessage.id)
              return // Ignorer ce message, on l'a déjà ajouté depuis la réponse API
            }
            
            // Vérifier aussi par texte si c'est un message utilisateur récent
            const messageTexte = getTexte(newMessage)
            console.log('[Socket.IO Listener] 🔍 Vérification du texte:', {
              messageTexte,
              dansRecentlySentTexts: recentlySentMessageTexts.value.has(messageTexte),
              qui: newMessage.qui
            })
            
            if (newMessage.qui === 'user' && recentlySentMessageTexts.value.has(messageTexte)) {
              console.log('[Socket.IO Listener] 🚫 Message utilisateur récent détecté par texte, mise à jour du temporaire')
              // C'est un message que nous venons d'envoyer, l'ignorer
              // Mais vérifier si on doit mettre à jour un message temporaire avec l'ID
              const existingTempMessageIndex = messages.value.findIndex(m => 
                m.id === 0 && 
                getTexte(m) === messageTexte && 
                m.qui === 'user'
              )
              
              if (existingTempMessageIndex >= 0 && newMessage.id) {
                console.log('[Socket.IO Listener] 🔄 Mise à jour du message temporaire avec l\'ID du message:', {
                  index: existingTempMessageIndex,
                  ancienId: messages.value[existingTempMessageIndex].id,
                  nouveauId: newMessage.id,
                  nouveauQui: newMessage.qui
                })
                
                // Mettre à jour le message temporaire avec l'ID du message
                messages.value[existingTempMessageIndex] = newMessage
                // Ajouter l'ID à la liste pour ignorer les futurs messages
                recentlySentMessageIds.value.add(newMessage.id)
                setTimeout(() => {
                  recentlySentMessageIds.value.delete(newMessage.id)
                }, 5000)
              }
              
              console.log('[Socket.IO Listener] ✅ Ignoré l\'ajout du message (déjà présent)')
              return // Ignorer l'ajout du message car on l'a déjà
            }
            
            // Vérifier si le message n'existe pas déjà pour éviter les doublons
            console.log('[Socket.IO Listener] 🔍 Vérification des doublons...')
            // Vérifier par ID d'abord, puis par texte et date si l'ID n'est pas encore défini
            const newMessageTexte = getTexte(newMessage)
            const messageExists = messages.value.some(m => {
              // Si les deux messages ont un ID, comparer par ID
              if (m.id && newMessage.id && m.id === newMessage.id) {
                return true
              }
              // Si c'est un message temporaire (id=0), vérifier par texte et date récente (dans les 5 dernières secondes)
              if (m.id === 0 && newMessage.id && getTexte(m) === newMessageTexte) {
                const messageDate = new Date(m.date)
                const newMessageDate = new Date(newMessage.date)
                const timeDiff = Math.abs(newMessageDate.getTime() - messageDate.getTime())
                // Si les messages ont le même texte et sont envoyés dans les 5 secondes, c'est probablement le même
                if (timeDiff < 5000) {
                  return true
                }
              }
              return false
            })
            
            if (!messageExists) {
              console.log('[Socket.IO Listener] ✅ Message non trouvé dans la liste, recherche d\'un message temporaire...')
              
              // Si c'est un message utilisateur avec id=0 qui existe déjà, mettre à jour son ID
              // Chercher un message temporaire avec le même texte et qui (user)
              const existingTempMessageIndex = messages.value.findIndex(m => 
                m.id === 0 && 
                getTexte(m) === newMessageTexte && 
                m.qui === newMessage.qui
              )
              
              if (existingTempMessageIndex >= 0 && newMessage.id) {
                console.log('[Socket.IO Listener] 🔄 Remplacement du message temporaire:', {
                  index: existingTempMessageIndex,
                  ancienId: messages.value[existingTempMessageIndex].id,
                  nouveauId: newMessage.id,
                  nouveauQui: newMessage.qui
                })
                
                // Remplacer complètement le message temporaire par le nouveau message
                messages.value[existingTempMessageIndex] = newMessage
              } else {
                // Vérifier une dernière fois s'il n'existe pas déjà par ID avant d'ajouter
                const alreadyExists = messages.value.some(m => 
                  m.id && newMessage.id && m.id === newMessage.id
                )
                
                if (!alreadyExists) {
                  console.log('[Socket.IO Listener] ➕ Ajout du nouveau message:', {
                    id: newMessage.id,
                    qui: newMessage.qui,
                    texte: newMessage.texte,
                    totalMessagesAvant: messages.value.length
                  })
                  
                  // Ajouter le nouveau message
                  messages.value.push(newMessage)
                  
                  console.log('[Socket.IO Listener] 📊 État après ajout:', {
                    totalMessagesApres: messages.value.length,
                    dernierMessage: {
                      id: messages.value[messages.value.length - 1]?.id,
                      qui: messages.value[messages.value.length - 1]?.qui,
                      texte: messages.value[messages.value.length - 1]?.texte
                    }
                  })
                } else {
                  console.log('[Socket.IO Listener] 🚫 Message déjà existant par ID, ignoré')
                }
              }
              
              // Mettre à jour les infos de l'agent si c'est un message du bot
              if (newMessage.qui === 'bot') {
                agentName.value = newMessage.agentNom || 'Service client'
                agentAvatar.value = newMessage.agentAvatar || '/public/assets/tchat/600w/avatar.jpg'
                isAgentTyping.value = false
              }
              
              scrollToBottom()
            }
          }
        }
        
        // Fonction pour enregistrer le listener et rejoindre le room
        const setupRoomAndListener = () => {
          console.log('[Socket.IO] 🔍 État du socket:', {
            connected: currentSocket.connected,
            id: currentSocket.id,
            roomName: roomName
          })
          
          // Enregistrer le listener DIRECTEMENT sur l'instance du socket (pas via getSocket())
          if (messageListener) {
            console.log('[Socket.IO] 👂 Enregistrement DIRECT du listener pour "new.message" sur le socket:', currentSocket.id)
            currentSocket.on('new.message', messageListener)
            console.log('[Socket.IO] ✅ Listener enregistré directement sur le socket')
          } else {
            console.error('[Socket.IO] ❌ Erreur: messageListener est null')
          }
          
          // Rejoindre le room
          if (currentSocket.connected) {
            console.log('[Socket.IO] 📥 Rejoignant le room:', roomName)
            currentSocket.emit('join', roomName)
            
            // Écouter la confirmation que le room a été rejoint
            currentSocket.once('joined', (data: any) => {
              console.log('[Socket.IO] ✅ Room rejoint avec succès:', data)
              console.log('[Socket.IO] 🧪 Le socket devrait maintenant recevoir les messages du room')
            })
          } else {
            // Attendre que le socket soit connecté
            console.log('[Socket.IO] ⏳ En attente de la connexion du socket...')
            currentSocket.once('connect', () => {
              console.log('[Socket.IO] ✅ Socket connecté, configurant le room et le listener...')
              setupRoomAndListener()
            })
          }
        }
        
        // Configurer le room et le listener
        setupRoomAndListener()
        
      } catch (error) {
        console.warn('[Socket.IO] ⚠️ Erreur lors de la configuration de Socket.IO:', error)
        // L'application continue à fonctionner sans temps réel
      }
    }

    /**
     * Arrêter l'écoute Socket.IO
     */
    const stopSocketListener = () => {
      if (currentRoomName) {
        try {
          leaveRoom(currentRoomName)
          if (messageListener && currentSocketInstance) {
            // Retirer le listener de la même instance de socket sur laquelle il a été enregistré
            currentSocketInstance.off('new.message', messageListener)
            console.log('[Socket.IO] 🗑️ Listener retiré du socket:', currentSocketInstance.id)
            messageListener = null
          }
        } catch (error) {
          console.warn('[Socket.IO] ⚠️ Erreur lors de la déconnexion:', error)
        }
        currentRoomName = null
        currentSocketInstance = null
      }
    }

    /**
     * Initialiser la conversation
     */
    const initConversation = async () => {
      // Arrêter l'ancienne écoute si elle existe
      stopSocketListener()

      const id = route.params.id as string
      const tag = route.query.tag as string
      const type = route.query.type as string

      if (tag) {
        selectedTag.value = tag
      }
      
      if (type) {
        conversationType.value = type
      }

      if (id && id !== 'new') {
        // Charger une conversation existante
        conversationId.value = parseInt(id)
        await loadMessages(conversationId.value)
        // Configurer l'écoute Socket.IO
        setupSocketListener()
      } else {
        // Nouvelle conversation - attendre le premier message
        conversationId.value = null
      }
    }

    // Surveiller les nouveaux messages pour faire défiler
    watch(() => messages.value.length, () => {
      scrollToBottom()
    })

    // Surveiller les changements de route
    watch(() => route.params.id, () => {
      initConversation()
    })

    onMounted(() => {
      initConversation()
    })

    onUnmounted(() => {
      // Arrêter l'écoute Socket.IO lors de la déconnexion du composant
      stopSocketListener()
    })

    return {
      messagesContainer,
      messages,
      newMessage,
      isSending,
      isAgentTyping,
      loading,
      agentName,
      agentAvatar,
      selectedTag,
      sendMessage,
      attachFile
    }
  }
})
</script>

<style scoped>
/* Les styles globaux sont déjà définis dans les fichiers CSS globaux */
</style>
